import React from 'react';
import { motion } from 'framer-motion';
import { useLang } from '../../context/LangContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };
const EASE = [0.16, 1, 0.3, 1];

// ─────────────────────────────────────────────────────────────
// Shows
// ─────────────────────────────────────────────────────────────
const NEXT_SHOW = {
  day: '20',
  month: '06',
  year: '26',
  city: 'BERLIN',
  cityCode: 'BER',
  venue: 'Micheline',
  country: 'DE',
  bill: 'Comptoir · restaurant — live set',
  doors: 'TBA',
  status: 'DETAILS',
  url: 'https://www.instagram.com/tsanga_music/',
};

const PAST_SHOWS = [
  { date: '15·05·25', city: 'BRU', venue: 'Le Cobra Jaune',          bill: 'Wavy Series #1 · première édition' },
  { date: '13·05·25', city: 'PAR', venue: 'La Mécanique Ondulatoire', bill: 'Live' },
  { date: '12·05·25', city: 'LIL', venue: 'Lokkaria',                bill: 'Live' },
  { date: '15·02·25', city: 'REN', venue: 'Bar Vilaines',            bill: 'Live' },
  { date: '14·02·25', city: 'BRE', venue: 'Astropolis',              bill: 'Live' },
  { date: '13·02·25', city: 'STB', venue: 'Le Cessonnais',           bill: 'Live · St-Brieuc' },
];

export default function Lives() {
  const { lang } = useLang();
  const t = T[lang]?.lives || T.fr.lives;

  return (
    <section
      id="lives"
      style={{
        position: 'relative',
        minHeight: '100vh',
        background: 'var(--bg)',
        padding: 'clamp(60px, 8vh, 100px) clamp(24px, 4vw, 56px) clamp(80px, 10vh, 120px)',
        overflow: 'hidden',
      }}
    >
      <div className="noise" aria-hidden="true" />

      {/* ── Chapter bar ─────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -10 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          paddingBottom: 18, borderBottom: '1px solid var(--line)', marginBottom: 'clamp(48px, 7vh, 80px)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
          <span style={{ fontFamily: 'var(--display)', fontSize: 32, fontWeight: 300 }}>IV.</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.22em', textTransform: 'uppercase' }}>{t.title}</span>
        </div>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.18em', color: 'var(--faint)', textTransform: 'uppercase' }}>
          NEXT — 01 / {String(PAST_SHOWS.length + 1).padStart(2, '0')}
        </span>
      </motion.div>

      {/* ── NEXT SHOW — hero block ─────────────────────────── */}
      <motion.article
        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-15%' }} transition={{ duration: 1, ease: EASE }}
        className="lives-next-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(180px, 22vw) 1fr minmax(180px, 18vw)',
          gap: 'clamp(28px, 4vw, 64px)',
          alignItems: 'end',
          paddingBottom: 'clamp(40px, 6vh, 72px)',
          borderBottom: '1px solid rgba(255,255,255,0.14)',
        }}
      >
        {/* Left — stacked date */}
        <div style={{ borderRight: '1px solid var(--line)', paddingRight: 'clamp(20px, 2vw, 40px)' }}>
          <div style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.28em',
            color: 'var(--faint)', textTransform: 'uppercase', marginBottom: 22,
          }}>
            {NEXT_SHOW.day} · JUN · 20{NEXT_SHOW.year}
          </div>
          <div style={{
            fontFamily: 'var(--display)', fontWeight: 200,
            fontSize: 'clamp(96px, 14vw, 200px)', lineHeight: 0.82,
            letterSpacing: '-0.03em', color: '#fff',
          }}>
            {NEXT_SHOW.day}
          </div>
          <div style={{
            marginTop: 8, fontFamily: 'var(--display)', fontWeight: 200,
            fontSize: 'clamp(40px, 5vw, 72px)', lineHeight: 1,
            letterSpacing: '-0.01em', color: 'rgba(255,255,255,0.55)',
          }}>
            ·{NEXT_SHOW.month}
          </div>
        </div>

        {/* Center — city + venue + bill */}
        <div>
          <div style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.28em',
            color: 'var(--faint)', textTransform: 'uppercase', marginBottom: 18,
          }}>
            {NEXT_SHOW.cityCode} / {NEXT_SHOW.country} — DOORS {NEXT_SHOW.doors}
          </div>
          <h3 style={{
            margin: 0, fontFamily: 'var(--display)', fontWeight: 200,
            fontSize: 'clamp(56px, 9vw, 132px)', lineHeight: 0.88,
            letterSpacing: '-0.035em', color: '#fff',
          }}>
            {NEXT_SHOW.city}
          </h3>
          <div style={{
            marginTop: 18, display: 'flex', alignItems: 'baseline',
            gap: 16, flexWrap: 'wrap',
          }}>
            <span style={{
              fontFamily: 'var(--display)', fontSize: 'clamp(20px, 2vw, 28px)',
              fontWeight: 300, color: 'rgba(255,255,255,0.92)',
            }}>
              {NEXT_SHOW.venue}
            </span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--faint)', letterSpacing: '0.2em' }}>—</span>
            <span style={{
              fontFamily: 'var(--mono)', fontSize: 12, lineHeight: 1.6,
              color: 'var(--muted)', letterSpacing: '0.04em',
            }}>
              {NEXT_SHOW.bill}
            </span>
          </div>
        </div>

        {/* Right — CTA */}
        <div style={{ textAlign: 'right' }}>
          <motion.a
            href={NEXT_SHOW.url}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.85)', color: '#fff' }}
            transition={{ duration: 0.25 }}
            aria-label={`${NEXT_SHOW.status} — ${NEXT_SHOW.venue}, ${NEXT_SHOW.city}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 14,
              padding: '16px 22px',
              border: '1px solid rgba(255,255,255,0.35)',
              fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.28em',
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)',
              textDecoration: 'none', cursor: 'none',
              background: 'transparent',
            }}
          >
            {NEXT_SHOW.status}
            <span aria-hidden style={{ fontSize: 14, lineHeight: 1 }}>→</span>
          </motion.a>
          <div style={{
            marginTop: 14, fontFamily: 'var(--mono)', fontSize: 10,
            letterSpacing: '0.22em', color: 'var(--faint)', textTransform: 'uppercase',
          }}>
            IG · @tsanga.world
          </div>
        </div>
      </motion.article>

      {/* ── PAST / ARCHIVE strip ────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-10%' }} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        style={{ marginTop: 'clamp(40px, 6vh, 72px)' }}
      >
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          marginBottom: 28,
        }}>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.28em',
            color: 'var(--faint)', textTransform: 'uppercase',
          }}>
            Archive — {String(PAST_SHOWS.length).padStart(2, '0')} past
          </span>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.22em',
            color: 'var(--faint)', textTransform: 'uppercase',
          }}>
            2025
          </span>
        </div>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {PAST_SHOWS.map((s) => (
            <li
              key={`${s.date}-${s.city}-${s.venue}`}
              className="lives-archive-row"
              style={{
                display: 'grid',
                gridTemplateColumns: '120px 64px 1fr 1.6fr',
                gap: 'clamp(16px, 2vw, 28px)',
                padding: '20px 0',
                borderTop: '1px solid var(--line)',
                alignItems: 'baseline',
              }}
            >
              <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em' }}>
                {s.date}
              </span>
              <span style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 300, color: 'rgba(255,255,255,0.7)' }}>
                {s.city}
              </span>
              <span style={{ fontFamily: 'var(--display)', fontSize: 18, fontWeight: 300, color: 'rgba(255,255,255,0.7)' }}>
                {s.venue}
              </span>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--faint)', letterSpacing: '0.04em' }}>
                {s.bill}
              </span>
            </li>
          ))}
        </ul>

        {/* Bottom whisper */}
        <div style={{
          marginTop: 'clamp(32px, 5vh, 56px)', paddingTop: 22,
          borderTop: '1px solid rgba(255,255,255,0.14)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
          flexWrap: 'wrap', gap: 16,
        }}>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.28em',
            color: 'var(--faint)', textTransform: 'uppercase',
          }}>
            {t.soon}
          </span>
          <a
            href="#contact"
            style={{
              fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.28em',
              color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase',
              textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.25)',
              paddingBottom: 4, cursor: 'none',
            }}
          >
            Booking · enquiries →
          </a>
        </div>
      </motion.div>
    </section>
  );
}
